import { createApi } from '@reduxjs/toolkit/query/react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { portalBaseQuery, sendWithAuthRetry } from '@lib/baseQuery';
import { services } from '@config/services';
import { getValidToken } from '@lib/getValidToken';
import { HttpError } from '@lib/httpError';

/** File metadata returned by GET /files/v1/files/{fileId}. Portal pulls these
 * out of `response.data.fileInfo` and inlines them under `merchantFileInfo`
 * and `vendorReadyFileInfo` on each image entry. */
export interface FileInfo {
  fileId?: string;
  url?: string;
  /** `unknown/unknown` for passthrough files the service couldn't identify. */
  contentType?: string;
  metaTags?: {
    Width?: string;
    Height?: string;
    Format?: string;
    /** Original uploaded filename; present on passthrough files even when Format is not. */
    origName?: string;
    /** Original source URL; same availability as origName. */
    origUrl?: string;
    [key: string]: string | undefined;
  };
  [key: string]: unknown;
}

export interface GetFileResponse {
  fileInfo: FileInfo;
}

/** What files-service reads from a stored image, as the orchestrator's image check sees it. */
export interface ImageInfo {
  /** Upper case ("PNG", "JPEG", "PDF", "DST"), except "Webp". */
  format: string;
  /** Pixels. Absent on passthrough files; a PDF's first page is measured at 300 DPI. */
  width?: number;
  height?: number;
  /** DST, or an AI or EPS file without a size. */
  isPassthrough: boolean;
  sizeInMiB?: number;
}

/** Placement fields the files-service manip endpoint composites onto the
 * canvas. `canvasWidth`/`canvasHeight` are the vendor print-area dimensions
 * (pixels), sourced from the merchant-catalog decoration matching the
 * image's area — the same lookup AdditionalItemDetails already uses for DPI. */
export interface ManipPlacementParams {
  canvasWidth: number;
  canvasHeight: number;
  x: number;
  y: number;
  scale: number;
  angle: number;
  previewMaxDimension?: number;
}

export interface ManipPreviewArgs extends ManipPlacementParams {
  fileId: string;
}

export const DEFAULT_PREVIEW_MAX_DIMENSION = 1024;

function buildManipUrl({ fileId, ...params }: ManipPreviewArgs): string {
  const search = new URLSearchParams({
    'placement.canvasWidth': String(params.canvasWidth),
    'placement.canvasHeight': String(params.canvasHeight),
    'placement.x': String(params.x),
    'placement.y': String(params.y),
    'placement.scale': String(params.scale),
    'placement.angle': String(params.angle),
    'placement.previewMaxDimension': String(params.previewMaxDimension ?? DEFAULT_PREVIEW_MAX_DIMENSION),
  });
  return `${services.files}/v1/filestream/${fileId}/manip?${search.toString()}`;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the preview image'));
    reader.readAsDataURL(blob);
  });
}

/**
 * getFile (metadata + read URL). Browser uploads use the `uploadFile` proxy
 * below, which validates content server-side. See TECH-22216.
 */
export const filesApi = createApi({
  reducerPath: 'filesApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['File'],
  endpoints: (builder) => ({
    getFile: builder.query<GetFileResponse, string>({
      query: (fileId) => ({
        url: `${services.files}/v1/files/${fileId}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, fileId) => [{ type: 'File', id: fileId }],
    }),
    /**
     * GET /files/v1/files/{fileId}/imageinfo
     * -> 200 { imageInfo: { height: 400, width: 600, format: 'PNG', isPassthrough: false, sizeInMiB: 0.1 } }
     * -> 404 when the file doesn't exist; 400 when its content isn't an image files-service reads.
     */
    getImageInfo: builder.query<ImageInfo, string>({
      query: (fileId) => ({
        url: `${services.files}/v1/files/${fileId}/imageinfo`,
        method: 'GET',
      }),
      transformResponse: (response: { imageInfo: ImageInfo }) => response.imageInfo,
      providesTags: (_r, _e, fileId) => [{ type: 'File', id: fileId }],
    }),
    /**
     * GET /files/v1/filestream/{fileId}/manip?placement.* -> the composed image bytes.
     * The bytes become a data URL so the cached value is serializable and needs no
     * revoking; a preview is never reused once its placement changes.
     */
    getManipPreview: builder.query<string, ManipPreviewArgs>({
      query: (args) => ({
        url: buildManipUrl(args),
        method: 'GET',
        responseHandler: (response) => (response.ok ? response.blob().then(blobToDataUrl) : response.text()),
      }),
      keepUnusedDataFor: 0,
    }),
    /**
     * POST /files/v1/files
     * body { fileCategory, public, uploadLater: false, sourceUrl, fileCategoryParams }
     * -> 201 { fileInfo: { fileId, url, ... } }
     * files-service copies the link server-side, so a link it can't fetch fails here with a 400
     * instead of later, in order processing.
     */
    createFileFromUrl: builder.mutation<UploadFileResult, CreateFileFromUrlArgs>({
      query: ({ sourceUrl, fileCategory, isPublic = false, fileCategoryParams }) => ({
        url: `${services.files}/v1/files`,
        method: 'POST',
        body: { fileCategory, public: isPublic, uploadLater: false, sourceUrl, fileCategoryParams },
      }),
    }),
  }),
});

export const { useGetFileQuery, useLazyGetFileQuery, useGetManipPreviewQuery, useCreateFileFromUrlMutation } = filesApi;

export interface CreateFileFromUrlArgs {
  sourceUrl: string;
  /** Files-service category, e.g. 'order-image'. */
  fileCategory: string;
  /** Public (permanent CDN URL) vs private (read SAS). Default private. */
  isPublic?: boolean;
  fileCategoryParams?: Record<string, string>;
}

export interface UploadFileOptions {
  /** Files-service category, e.g. 'order-image', 'plugin-logos'. */
  fileCategory: string;
  /** Public (permanent CDN URL) vs private (read SAS, valid 60 days). Default private. */
  isPublic?: boolean;
  fileCategoryParams?: Record<string, unknown>;
  metaTags?: Record<string, unknown>;
  /** Integer-percent upload progress (throttled to changes). */
  onProgress?: (pct: number) => void;
  /** Cancels the in-flight upload; a removed AbortController aborts the XHR. */
  signal?: AbortSignal;
}

export interface UploadFileResult {
  fileInfo: {
    fileId: string;
    url: string;
    contentMd5?: string | null;
    [key: string]: unknown;
  };
}

const uploadAbortError = (): Error => {
  const err = new Error('Upload aborted');
  err.name = 'AbortError';
  return err;
};

/**
 * Upload a file via the files-service validating proxy (POST /v1/files/upload).
 * XHR, not fetch/RTK, so callers get upload progress + cancellation. See TECH-22216.
 * A 401/403 is retried once on a refreshed token, like every RTK request.
 */
export function uploadFile(file: File, options: UploadFileOptions): Promise<UploadFileResult> {
  return sendWithAuthRetry(() => sendUpload(file, options));
}

async function sendUpload(file: File, options: UploadFileOptions): Promise<UploadFileResult> {
  const { fileCategory, isPublic = false, fileCategoryParams, metaTags, onProgress, signal } = options;
  if (signal?.aborted) throw uploadAbortError();

  const token = await getValidToken();
  if (signal?.aborted) throw uploadAbortError();

  const form = new FormData();
  form.append('File', file, file.name || 'upload');
  form.append('FileCategory', fileCategory);
  form.append('Public', String(Boolean(isPublic)));
  if (fileCategoryParams) form.append('FileCategoryParams', JSON.stringify(fileCategoryParams));
  if (metaTags) form.append('MetaTags', JSON.stringify(metaTags));

  return new Promise<UploadFileResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${services.files}/v1/files/upload`, true);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    // No Content-Type — the browser sets the multipart boundary for FormData.

    let lastPct = -1;
    xhr.upload.onprogress = (e): void => {
      if (!onProgress || !e.lengthComputable) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      if (pct === lastPct) return;
      lastPct = pct;
      onProgress(pct);
    };
    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadFileResult);
        } catch {
          reject(new Error('Upload succeeded but the response was not valid JSON'));
        }
      } else {
        reject(new HttpError(`Upload failed: ${xhr.status} ${xhr.statusText}`, xhr.status));
      }
    };
    xhr.onerror = (): void => reject(new Error('Upload failed: network error'));
    xhr.onabort = (): void => reject(uploadAbortError());
    if (signal) {
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}

/** Image entry shape that the order-item exposes. Only the file-id fields
 * matter for image-info resolution; everything else passes through. */
export interface ImageWithFileIds {
  ourFileId?: string;
  vendorReadyFileId?: string;
}

export interface EnrichedImageInfo {
  /** Original image entry, unchanged. */
  [key: string]: unknown;
  merchantFileInfo: FileInfo | null;
  vendorReadyFileInfo: FileInfo | null;
}

/**
 * Mirrors the portal saga `fetchImageInfo` (sagas/files.js). For each image
 * with `ourFileId` and/or `vendorReadyFileId`, it fetches the metadata via
 * GET /files/v1/files/{fileId} and returns `[{...image, merchantFileInfo,
 * vendorReadyFileInfo },...]`. Errors are swallowed per-image — portal
 * returns null for the slot it couldn't fetch, so the UI can fall back.
 * RTK Query doesn't compose well for "fan out N requests, await all," so
 * this hook drives `store.dispatch(filesApi.endpoints.getFile.initiate())`
 * directly and caches results in component state. Returns null while
 * loading so the parent can show a spinner; matches portal's gate on
 * `!imageDetails`.
 */
// Locally-typed dispatch covering the thunk + plain-action union that
// RTK Query's `initiate()` returns. Avoids importing `AppDispatch` (which
// would create a `files/api → app/store → files/api` cycle).
type ThunkAwareDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

export function useImageInfoList(images: ImageWithFileIds[] | undefined): EnrichedImageInfo[] | null {
  // RTK Query's `useLazyQuery` hooks can't be called in a loop (one per
  // image), so we drive the lazy query via `dispatch(endpoint.initiate(...))`.
  // The typed dispatch lets us drop the `as any` cast that was here before.
  const dispatch = useDispatch<ThunkAwareDispatch>();
  const [result, setResult] = useState<EnrichedImageInfo[] | null>(null);

  useEffect(() => {
    if (!images || images.length === 0) {
      setResult(images?.length === 0 ? [] : null);
      return;
    }
    let cancelled = false;
    setResult(null);
    (async (): Promise<void> => {
      const enriched = await Promise.all(
        images.map(async (image): Promise<EnrichedImageInfo> => {
          let merchantFileInfo: FileInfo | null = null;
          let vendorReadyFileInfo: FileInfo | null = null;
          if (image.ourFileId) {
            try {
              const { data } = await dispatch(filesApi.endpoints.getFile.initiate(image.ourFileId));
              merchantFileInfo = data?.fileInfo ?? null;
            } catch {
              merchantFileInfo = null;
            }
          }
          if (image.vendorReadyFileId) {
            try {
              const { data } = await dispatch(filesApi.endpoints.getFile.initiate(image.vendorReadyFileId));
              vendorReadyFileInfo = data?.fileInfo ?? null;
            } catch {
              vendorReadyFileInfo = null;
            }
          }
          return {...image, merchantFileInfo, vendorReadyFileInfo };
        }),
      );
      if (!cancelled) setResult(enriched);
    })();
    return () => {
      cancelled = true;
    };
    // The `images` array is recreated on every render of the parent, so we
    // key on a stable signature instead of object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images?.map((i) => `${i.ourFileId ?? ''}|${i.vendorReadyFileId ?? ''}`).join(';')]);

  return result;
}
